import React, { useEffect, useState } from "react";
import { Button, Modal } from "src/components";
import {
  Container,
  DetailsContainer,
  Inner,
  BankInfo,
  FormContainer,
  HeaderNav,
  InnerFrame,
  Actions,
  SubmitNote,
} from "./style";
import { Details } from "./details";
import { useFormik } from "formik";
import { addBankAccount } from "src/action/deposit";
import { BankAccountForm } from "src/components/BankAccountForm";
import {
  bankAccountInitialValues,
  bankAccountSchema,
} from "src/components/BankAccountForm/schema";
import { ErrorMessage } from "src/components/Deposit/styles/deposit";
import {
  DownloadIcon,
  ImageContainer,
} from "../Details/styles/styleDetailsFinaAccount";
import download from "src/assets/download.svg";
import { useWindowSize } from "src/hooks/useWindowSize";
import { useTheme } from "styled-components";

/**
 * ONE dialog, whichever flow opened it.
 *
 * It briefly had two faces: opened from the withdraw drawer it dropped the
 * panel of FinaMaze's own account details and the PDF beside the header,
 * on the argument that both are for someone sending money IN. The argument
 * was about the CONTENT and the cost was the shape — the same act, "add my
 * bank account", met the reader as a 480px form on one page and an 880px
 * two-pane dialog on the other, and a reader who had added an account once
 * did not recognise the thing they were being shown the second time.
 *
 * Consistency wins: registering a bank account looks the same everywhere it
 * is offered. The details panel is a reference rather than an instruction —
 * it is where the money comes FROM, which is a fact about the same account
 * the form is registering — and the form's own copy says what the form is
 * for.
 */
export const ManualBankModel = ({
  visible,
  onCancel,
  client,
}: {
  visible: boolean;
  onCancel: () => void;
  client?: any;
}) => {
  const [error, setError] = useState("");
  /**
   * A fixed width, sized to what is in it.
   *
   * It was 70% of the window on a desktop, so the same six fields sat in a
   * 900px dialog on a wide monitor and a 750px one on a laptop, and the form
   * inside it was a 300px strip either way. 880px: the form at the drawer's
   * measure, with FinaMaze's own account details in a pane beside it.
   */
  const WIDTH = 880;
  const modalRef = React.useRef(WIDTH);
  const { width } = useWindowSize();
  const formik = useFormik({
    initialValues: {
      ...bankAccountInitialValues,
    },
    onSubmit: async (values) => {
      const res = await addBankAccount(values);
      if (res.success) {
        onCancel();
      } else {
        setError(res.message);
      }
    },

    validationSchema: bankAccountSchema,
  });

  useEffect(() => {
    modalRef.current = width < WIDTH + 48 ? width - 32 : WIDTH;
  }, [width]);
  const isSubmitAllowed = formik.isValid && formik.dirty;
  const theme = useTheme();
  return (
    <Modal
      bodyStyle={{ padding: 0 }}
      /* The shared Modal already carries the dialog chrome — head with a
         hairline, close control, foot band. This opted out of it with
         visibleHeader={false} and drew its own heading inside the body, so
         the one dialog you reach from the deposit dialog looked unlike it. */
      title="New bank account"
      visible={visible}
      footer={null}
      width={modalRef.current}
      onCancel={onCancel}
      contentPadding="0px"
    >
      <Container>
        <DetailsContainer>
          <Details client={client} />
        </DetailsContainer>
        <InnerFrame>
          <Inner>
            {/* The heading said "Standard transfer" and the line under it
                said the account was "to receive your future withdrawals" —
                both left over from somewhere else. Reached from the deposit
                dialog, this is the account money is sent FROM, and the same
                dialog serves withdrawals, so it says what is true of both.

                The PDF beside the old heading is FinaMaze's own account
                details, which is where a reader sends money — nothing to do
                with adding theirs. It keeps its link, named for what it is,
                rather than sitting unlabelled next to a title. */}
            <BankInfo>
              An account in your name, used to send deposits from and to
              receive withdrawals.{" "}
              <ImageContainer
                title="FinaMaze account details (PDF)"
                target="_blank"
                href="https://storage.googleapis.com/finamaze-integration/FinaMaze%20Account%20Details%20for%20Client%20Deposits%20(1).pdf"
              >
                FinaMaze account details (PDF)
              </ImageContainer>
            </BankInfo>
            {error && (
              <div style={{ padding: "20px 0", width: "100%" }}>
                <ErrorMessage
                  message="Error"
                  description={error}
                  type="error"
                  showIcon
                />
              </div>
            )}
            <FormContainer>
              {/* The only copy of these fields — the Deposit page's dialog
                  renders the same component. */}
              <BankAccountForm formik={formik} />
              {/* `style={{ height: "36" }}` had no unit, so the browser
                  dropped the declaration; the shared button's own height is
                  correct anyway. And the label carried a
                  circled-right-arrow — a glyph for going onward, on a
                  control that saves and closes. */}
              <Actions>
                <Button
                  type="submit"
                  width="100%"
                  disabled={!isSubmitAllowed || formik.isSubmitting}
                  loading={formik.isSubmitting || undefined}
                  onClick={formik.handleSubmit}
                >
                  Save bank account
                </Button>
                <SubmitNote>
                  Withdrawals can only be sent to an account in your own name.
                </SubmitNote>
              </Actions>
            </FormContainer>
          </Inner>
        </InnerFrame>
      </Container>
    </Modal>
  );
};
